<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useResizeObserver } from '@vueuse/core';
import { TRIGGER_EVENTS, TRIGGER_CATEGORIES } from '../constants/nodeTypes.js';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['select', 'close']);

const { t } = useI18n();
const dialogRef = ref(null);
const search = ref('');
const activeFilter = ref(0);

watch(
  () => props.show,
  val => {
    if (val) {
      search.value = '';
      activeFilter.value = 0;
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);

const filteredEvents = computed(() => {
  const category = TRIGGER_CATEGORIES[activeFilter.value].key;
  const q = search.value.toLowerCase();
  return TRIGGER_EVENTS.filter(e => {
    const matchesCategory = category === 'all' || e.category === category;
    const matchesSearch = !q || t(e.label).toLowerCase().includes(q) || t(e.description).toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });
});

const tabRefs = ref([]);
const indicatorStyle = ref({});
const enableTransition = ref(false);

const activeElement = computed(() => tabRefs.value[activeFilter.value]);

const updateIndicator = () => {
  nextTick(() => {
    if (!activeElement.value) return;
    indicatorStyle.value = {
      left: `${activeElement.value.offsetLeft}px`,
      width: `${activeElement.value.offsetWidth}px`,
    };
  });
};

useResizeObserver(activeElement, updateIndicator);
watch([activeFilter, () => props.show], updateIndicator, { immediate: true });

onMounted(() => {
  nextTick(() => { enableTransition.value = true; });
});

const showDivider = index =>
  (index > activeFilter.value && index < TRIGGER_CATEGORIES.length - 1) ||
  (index < activeFilter.value - 1 && index > -1);

function onSelect(event) {
  emit('select', event.key);
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('FLOW_BUILDER.TRIGGER_PICKER.TITLE')"
    width="2xl"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="$emit('close')"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="search"
        type="search"
        :placeholder="t('FLOW_BUILDER.TRIGGER_PICKER.SEARCH_PLACEHOLDER')"
        :custom-input-class="['ltr:!pl-8']"
        autofocus
      >
        <template #prefix>
          <span class="i-lucide-search absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2" />
        </template>
      </Input>

      <!-- Category tabs -->
      <div class="relative flex items-center h-8 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 w-full has-[button:active]:scale-[1.01] [&>button]:flex-1">
        <div
          class="absolute rounded-lg bg-n-solid-active shadow-sm pointer-events-none h-8 outline-1 outline outline-n-container inset-y-0"
          :class="{ 'transition-all duration-300 ease-out': enableTransition }"
          :style="indicatorStyle"
        />
        <template v-for="(cat, index) in TRIGGER_CATEGORIES" :key="cat.key">
          <button
            :ref="el => (tabRefs[index] = el)"
            type="button"
            class="relative z-10 px-3 truncate py-1.5 text-sm border-0 outline-1 outline-transparent rounded-lg transition-all duration-200 ease-out hover:text-n-brand active:scale-[1.02]"
            :class="activeFilter === index ? 'text-n-blue-11' : 'text-n-slate-10 scale-[0.98]'"
            @click="activeFilter = index"
          >
            {{ t(cat.label) }}
          </button>
          <div
            v-if="index < TRIGGER_CATEGORIES.length - 1"
            class="w-px h-3.5 rounded my-auto transition-colors duration-300 ease-in-out"
            :class="showDivider(index) ? 'bg-n-strong' : 'bg-transparent'"
          />
        </template>
      </div>

      <!-- Events grid -->
      <div
        v-if="filteredEvents.length === 0"
        class="flex items-center justify-center py-8 text-sm text-n-slate-9"
      >
        {{ t('FLOW_BUILDER.TRIGGER_PICKER.EMPTY') }}
      </div>

      <div v-else class="grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-1">
        <button
          v-for="event in filteredEvents"
          :key="event.key"
          type="button"
          class="relative bg-n-solid-1 cursor-pointer rounded-2xl flex flex-col justify-start transition-all duration-200 ease-in py-5 px-4 items-start border border-solid border-n-weak hover:border-violet-400 hover:shadow-md text-left"
          @click="onSelect(event)"
        >
          <div class="flex items-start gap-3 w-full">
            <span class="flex items-center justify-center size-8 rounded-lg bg-n-violet-3 shrink-0 mt-0.5">
              <span :class="[event.icon, 'size-4 text-n-violet-9']" />
            </span>
            <div class="flex flex-col items-start gap-1.5 min-w-0">
              <h3 class="text-n-slate-12 text-sm font-medium">{{ t(event.label) }}</h3>
              <p v-if="event.description" class="text-n-slate-11 text-sm">{{ t(event.description) }}</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  </Dialog>
</template>
